import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import fileDownload from 'js-file-download';
import Toast from '../components/Toast';
import { jsonDelete, jsonGet, jsonPost, jsonPut } from '../api/jsonRequest';
import { getAuthConfig } from '../mixins/ApiConfig';
import {
    EMPTY_GUID,
    FILE_PROCESS_POLL_INTERVAL_MS,
    refetchIntervalWithActiveProcessing,
} from '../query/queryConfig';
import { queryKeys } from '../query/queryKeys';
import { buildPollUrl } from '../query/queryParams';
import { usePollQuery } from '../query/usePollQuery';
import { getUploadErrorMessage } from '../utils/uploadLimits';
import { notifyFileCountLimits } from '../utils/fileCountAlerts';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function invalidateAllFiles(queryClient) {
    void queryClient.invalidateQueries({ queryKey: queryKeys.companyFiles.all() });
    void queryClient.invalidateQueries({ queryKey: queryKeys.files.all() });
}

async function refreshAllFiles(queryClient) {
    await queryClient.invalidateQueries({ queryKey: queryKeys.companyFiles.all() });
    await queryClient.invalidateQueries({ queryKey: queryKeys.files.all() });
}

function uploadErrorToast(error, fallbackTitle) {
    const sizeMessage = getUploadErrorMessage(error);
    Toast.fire({
        title: sizeMessage || fallbackTitle,
        icon: 'error',
    });
}

function formDataUploadConfig() {
    const config = getAuthConfig();
    const fileUploadConfig = { ...config };
    delete fileUploadConfig.headers['Content-Type'];
    return fileUploadConfig;
}

export function getCompanyFilesUrl(companyId, customerId, page, pageSize) {
    return buildPollUrl(
        '/api/File/GetAllFilesForCompany',
        companyId,
        customerId,
        page,
        pageSize,
    );
}

export function useCompanyFiles({
    companyId,
    customerId,
    page,
    pageSize,
    enabled = true,
    pauseRefetch = false,
}) {
    const resolvedCustomerId = customerId ?? EMPTY_GUID;
    const url = getCompanyFilesUrl(companyId, resolvedCustomerId, page, pageSize);

    return usePollQuery({
        queryKey: queryKeys.companyFiles.list(companyId, resolvedCustomerId, page, pageSize),
        url,
        enabled,
        pauseRefetch,
        refetchInterval: refetchIntervalWithActiveProcessing,
    });
}

export function useCustomerFiles({
    companyId,
    customerId,
    page,
    pageSize,
    enabled = true,
    pauseRefetch = false,
}) {
    const url = buildPollUrl(
        '/api/File/GetAllFilesForCustomer',
        companyId,
        customerId,
        page,
        pageSize,
    );

    return usePollQuery({
        queryKey: queryKeys.files.customerList(companyId, customerId, page, pageSize),
        url,
        enabled,
        pauseRefetch,
        refetchInterval: refetchIntervalWithActiveProcessing,
    });
}

export function useCompanyFilesInbox({ companyId, enabled = true, pauseRefetch = false }) {
    const url = buildPollUrl(
        '/api/File/GetAllFilesForCompany',
        companyId,
        EMPTY_GUID,
        0,
        100,
    );

    return usePollQuery({
        queryKey: queryKeys.companyFiles.list(companyId, EMPTY_GUID, 0, 100),
        url,
        enabled,
        pauseRefetch,
        refetchInterval: refetchIntervalWithActiveProcessing,
    });
}

export function useDeleteFiles() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (fileIds) => jsonDelete(`/api/File/DeleteFile/${JSON.stringify(fileIds)}`),
        onSuccess: () => {
            invalidateAllFiles(queryClient);
        },
    });
}

export function useFileCount({ companyId, enabled = true, notify = true }) {
    const url = buildPollUrl('/api/File/CheckFileLimit', companyId);
    const lastNotifiedRef = React.useRef(null);

    const query = useQuery({
        queryKey: queryKeys.files.count(companyId),
        queryFn: () => jsonGet(url),
        enabled: Boolean(companyId) && enabled,
        staleTime: 0,
    });

    React.useEffect(() => {
        if (!notify || query.data == null) {
            return;
        }
        if (lastNotifiedRef.current === query.data) {
            return;
        }
        lastNotifiedRef.current = query.data;
        notifyFileCountLimits(query.data);
    }, [notify, query.data]);

    return query;
}

export function useUploadFileNoProcess() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ file, companyId, name, customerId }) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('companyId', companyId);
            formData.append('name', name);
            formData.append('customerId', customerId);
            const res = await axios.post(
                '/api/File/FileUploadNoProcess',
                formData,
                formDataUploadConfig(),
            );
            if (res.status != 200) {
                throw new Error('Upload failed');
            }
            return res.data;
        },
        onSuccess: () => {
            Toast.fire({ title: 'File Uploaded', icon: 'success' });
            invalidateAllFiles(queryClient);
        },
        onError: (error) => {
            uploadErrorToast(error, 'Error with Uploading. Please try again.');
        },
    });
}

/** Polls processor; returns validation payloads for the caller to put into Redux. */
export function usePointCountPost() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload) => {
            const config = getAuthConfig();
            const postedFile = await axios.post('/api/File/FileProcessor', payload, config);
            let done = false;
            let fileInfo = null;
            while (!done) {
                await sleep(FILE_PROCESS_POLL_INTERVAL_MS);
                const final = await axios.get(`/api/File/GetFileInfo/${postedFile.data}`, config);
                fileInfo = final;
                if (final.data.isProcessed == 1) {
                    done = true;
                }
            }
            const newInfo = await axios.get(`/api/File/GetFileInfo/${postedFile.data}`, config);
            const validation = await axios.get(
                `/api/File/DownloadValidationReport/${postedFile.data}`,
                config,
            );
            const summary = await axios.get(
                `/api/File/DownloadSummaryReport/${postedFile.data}`,
                config,
            );
            return {
                newInfo: newInfo.data,
                validation: validation.data,
                summary: summary.data,
                sequencingErrorCode: fileInfo.data.sequencingErrorCode,
                fileInfo: fileInfo.data,
            };
        },
        onSuccess: (result) => {
            if (result.newInfo.sequencingErrorCode == 0 || result.newInfo.sequencingErrorCode == 1) {
                Toast.fire({ title: 'File Uploaded', icon: 'success' });
            } else if (result.newInfo.sequencingErrorCode == 20) {
                Toast.fire({ title: 'File Uploaded, warnings found', icon: 'warning' });
            } else {
                Toast.fire({ title: 'Critical Error in file', icon: 'error' });
            }
            invalidateAllFiles(queryClient);
        },
        onError: (error) => {
            Toast.fire({ title: `${error}`, icon: 'error' });
        },
    });
}

export function useAcceptFile() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, acceptance }) => {
            const res = await jsonPost(`/api/File/AcceptFilePlan/${id}/${acceptance}`, {});
            if (res.status !== 200) {
                throw new Error('Error with file');
            }
            return { acceptance };
        },
        onSuccess: async ({ acceptance }) => {
            if (acceptance == 1) {
                Toast.fire({ title: 'File accepted', icon: 'success' });
            } else if (acceptance == 0) {
                Toast.fire({
                    title: 'Plan Not Accepted, Botmark Notified',
                    icon: 'success',
                });
            } else if (acceptance == 2) {
                Toast.fire({ title: 'Plan Rejected and Deleted', icon: 'success' });
            }
            await refreshAllFiles(queryClient);
        },
        onError: () => {
            Toast.fire({ title: 'Error with file', icon: 'error' });
        },
    });
}

export function useBuyFile() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (fileId) => {
            const res = await jsonPost(`/api/File/MarkFileAsPaid/${fileId}`, {});
            return res.data;
        },
        onSuccess: () => {
            Toast.fire({ title: 'Thank you for purchase', icon: 'success' });
            invalidateAllFiles(queryClient);
        },
        onError: () => {
            Toast.fire({ title: 'Error processing transaction' });
        },
    });
}

export function useUpdateFileName() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload) => {
            const res = await jsonPut(`/api/File/UpdateFileName/${payload.id}`, payload);
            if (res.status !== 200) {
                throw new Error('Error with file name change');
            }
            return payload.id;
        },
        onSuccess: () => {
            Toast.fire({ title: 'File Name Updated.', icon: 'success' });
            invalidateAllFiles(queryClient);
        },
        onError: () => {
            Toast.fire({
                title: 'Error with file name change. File name may already be in use, try again.',
                icon: 'error',
            });
        },
    });
}

export function useUpdateFileDownload() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, allowDownload }) => {
            const res = await jsonPut(
                `/api/File/UpdateFileAllowDownload/${id}`,
                allowDownload,
            );
            if (res.status !== 200) {
                throw new Error('Error with setting update');
            }
            return id;
        },
        onSuccess: () => {
            Toast.fire({ title: 'File Name Updated.', icon: 'success' });
            invalidateAllFiles(queryClient);
        },
        onError: () => {
            Toast.fire({ title: 'Error with setting update.', icon: 'error' });
        },
    });
}

export function useUpdateAllFilesAllowDownload() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ fileCustomerId, allowDownload }) => {
            if (!fileCustomerId) {
                return null;
            }
            const res = await jsonPut(
                `/api/File/UpdateInboxFilesAllowDownload/${fileCustomerId}`,
                allowDownload,
            );
            if (res.status !== 200) {
                throw new Error('Error updating allow download');
            }
            return res.data;
        },
        onSuccess: (_data, variables) => {
            if (!variables?.fileCustomerId) {
                return;
            }
            Toast.fire({ title: 'Allow download updated.', icon: 'success' });
            if (variables.refresh) {
                invalidateAllFiles(queryClient);
            }
        },
        onError: () => {
            Toast.fire({ title: 'Error updating allow download.', icon: 'error' });
        },
    });
}

export function useSetCustomerForFile() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ file, customer }) => {
            const res = await jsonPut(
                `/api/File/UpdateFileWithCustomerInfo/${file}/${customer}`,
            );
            if (res.status !== 200) {
                throw new Error('Error with setting a customer');
            }
            return res.data;
        },
        onSuccess: () => {
            Toast.fire({ title: 'Customer Set.', icon: 'success' });
            // Mark lists stale without refetching now — estimate/validation overlays
            // remount and drop Autocomplete state when lists refetch mid-interaction.
            void queryClient.invalidateQueries({
                queryKey: queryKeys.companyFiles.all(),
                refetchType: 'none',
            });
            void queryClient.invalidateQueries({
                queryKey: queryKeys.files.all(),
                refetchType: 'none',
            });
        },
        onError: () => {
            Toast.fire({
                title: 'Error with setting a customer to this Project.',
                icon: 'error',
            });
        },
    });
}

/** Downloads validation reports; invalidates files only when sequencing errors exist. */
export function useDownloadValidation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (fileInfo) => {
            const config = getAuthConfig();
            const name = fileInfo.name.split('.')[0];
            const validation = await axios.get(
                `/api/File/DownloadValidationReport/${fileInfo.id}`,
                config,
            );
            const summary = await axios.get(
                `/api/File/DownloadSummaryReport/${fileInfo.id}`,
                config,
            );
            fileDownload(validation.data, `${name}-ValidationReport.txt`);
            fileDownload(summary.data, `${name}-SequenceSummary.txt`);
            return {
                sequencingErrorCode: fileInfo.sequencingErrorCode,
            };
        },
        onSuccess: (result) => {
            if (result.sequencingErrorCode !== 0 && result.sequencingErrorCode !== 1) {
                invalidateAllFiles(queryClient);
            }
        },
    });
}

function pdfFormatControlHelper(file, name, zip) {
    if (file.status === 200 ? file.data : null) {
        const pdfFile = new Blob([file.data], { type: 'application/pdf' });
        zip.file(`${name}.pdf`, pdfFile);
    } else {
        console.log('No pdf file found.');
    }
}

function blobDownloadConfig(config) {
    return {
        headers: {
            Authorization: config?.headers?.Authorization,
        },
        responseType: 'blob',
    };
}

async function addLegacyDwgToZip(zip, name, fileId, config) {
    const dwgResponse = await axios.get(
        `/api/File/DownloadPaidDwg/${fileId}`,
        blobDownloadConfig(config),
    );
    if (dwgResponse.status !== 200 || dwgResponse.data == null) {
        return;
    }

    zip.file(`${name}.dwg`, dwgResponse.data);
}

async function addSecondaryFilesToZip(zip, name, fileId, config) {
    const extraFiles = await axios.get(`/api/File/GetExtraFileNames/${fileId}`, config);
    if (extraFiles.data === 'File count is zero' || !extraFiles.data?.length) {
        return;
    }

    const JSZip = require('jszip');
    for (const secondaryFile of extraFiles.data) {
        const secondaryFileName = secondaryFile.fileName || '';
        const response = await axios.get(
            `/api/File/DownloadPaidPDFSecondaryFile/${secondaryFile.id}`,
            blobDownloadConfig(config),
        );
        if (response.status !== 200 || response.data == null) {
            continue;
        }

        if (secondaryFileName.match(/\.zip$/i)) {
            const companionZip = await JSZip.loadAsync(response.data);
            for (const relativePath of Object.keys(companionZip.files)) {
                const fileEntry = companionZip.files[relativePath];
                if (!fileEntry.dir) {
                    zip.file(relativePath, await fileEntry.async('blob'));
                }
            }
        } else if (secondaryFileName.match(/\.pdf$/i)) {
            pdfFormatControlHelper(response, name, zip);
        }
    }
}

/** Processed plan upload — stores point list in FileReducer. */
export function useUploadFile() {
    const dispatch = useDispatch();

    return useMutation({
        mutationFn: async ({ file, companyId, name }) => {
            const config = formDataUploadConfig();
            const formData = new FormData();
            formData.append('file', file);
            formData.append('companyId', companyId);
            formData.append('name', name);
            const res = await axios.post('/api/File/FileUpload', formData, config);
            return res.data;
        },
        onSuccess: (data) => {
            dispatch({ type: 'SET_FILE_POINTS', payload: data });
        },
        onError: (error) => {
            uploadErrorToast(error, 'Error with Uploading. Make sure it is a .txt or .zip. Please try again.');
        },
    });
}

/** Loads validation report + summary + file info into FileReducer. */
export function useFetchValidationFile() {
    const dispatch = useDispatch();

    return useMutation({
        mutationFn: async (fileId) => {
            dispatch({ type: 'REPORT_LOADING' });
            const config = getAuthConfig();
            const [validation, summary, file] = await Promise.all([
                axios.get(`/api/File/DownloadValidationReport/${fileId}`, config),
                axios.get(`/api/File/DownloadSummaryReport/${fileId}`, config),
                axios.get(`/api/File/GetFileInfo/${fileId}`, config),
            ]);
            return {
                validation: validation.data,
                summary: summary.data,
                file: file.data,
            };
        },
        onSuccess: ({ validation, summary, file }) => {
            dispatch({ type: 'SUMMARY_FILE', payload: summary });
            dispatch({ type: 'VALIDATION_FILE', payload: validation });
            dispatch({ type: 'VALIDATION_ERROR', payload: file.sequencingErrorCode });
            dispatch({ type: 'VALIDATION_INFO', payload: file });
            dispatch({ type: 'REPORT_DONE' });
        },
        onError: (error) => {
            console.log(error);
            dispatch({ type: 'REPORT_DONE' });
        },
    });
}

/** Paid plan download (zip / skip-processed blob). */
export function useDownloadPaid() {
    return useMutation({
        mutationFn: async (payload) => {
            const config = getAuthConfig();
            const JSZip = require('jszip');

            if (payload.isProcessed === 2) {
                const hasLegacyDwg = payload.fileHasDWG === true;
                const hasSecondaryFiles = payload.hasSecondaryFiles === true;

                if (!hasSecondaryFiles && !hasLegacyDwg) {
                    const blobResponse = await axios.get(
                        `/api/File/DownloadSkipProcessedFile/${payload.id}`,
                        blobDownloadConfig(config),
                    );
                    fileDownload(blobResponse.data, payload.name);
                    return;
                }
            }

            const name = payload.name.split('.')[0];
            const zip = new JSZip();

            if (payload.name.match(/\.pdf$/)) {
                const PDFFile = await axios.get(
                    `/api/File/DownloadPaidPDFFile/${payload.id}`,
                    blobDownloadConfig(config),
                );
                pdfFormatControlHelper(PDFFile, name, zip);
            } else if (payload.name.match(/\.dwg$/)) {
                await addLegacyDwgToZip(zip, name, payload.id, config);
            } else if (payload.name.match(/\.txt$/) || payload.name.match(/\.zip$/)) {
                const txtFile = await axios.get(`/api/File/DownloadPaidTxt/${payload.id}`, config);
                if (txtFile.status == 200) {
                    zip.file(`${name}.txt`, txtFile.data);
                }
            }

            const bmkFile = await axios.get(`/api/File/DownloadBMK/${payload.id}`, config);
            if (bmkFile.status == 200 && bmkFile.data != null) {
                zip.file(`${name}.bmk`, bmkFile.data);
            }

            await addSecondaryFilesToZip(zip, name, payload.id, config);

            if (payload.fileHasDWG == true) {
                await addLegacyDwgToZip(zip, name, payload.id, config);
            }

            const content = await zip.generateAsync({ type: 'blob' });
            fileDownload(content, `${name}.zip`);
        },
        onSuccess: () => {
            Toast.fire({ icon: 'success', title: 'File(s) Found for Download' });
        },
    });
}

export function useUploadPdfFile() {
    return useMutation({
        mutationFn: async ({ file, companyId, name, PDFType, setFile, setAgType, navigate }) => {
            const config = formDataUploadConfig();
            const formData = new FormData();
            formData.append('file', file);
            formData.append('companyId', companyId);
            formData.append('name', name);
            formData.append('PDFType', PDFType);
            const res = await axios.post('/api/PDFFile/PDFFileUpload', formData, config);
            if (res.status !== 200) {
                throw new Error('Upload failed');
            }
            return { setFile, setAgType, navigate };
        },
        onSuccess: ({ setFile, setAgType, navigate }) => {
            Toast.fire({ title: 'PDF Uploaded', icon: 'success' });
            setFile?.([]);
            setAgType?.();
            navigate?.('/upload');
        },
        onError: (error) => {
            uploadErrorToast(error, 'Error in upload');
        },
    });
}

export function useFetchPolicyPdf() {
    const dispatch = useDispatch();

    return useMutation({
        mutationFn: async ({ pdfType, setPdfUrl, signal } = {}) => {
            // Do not toggle REPORT_LOADING — that flag blanks validation/estimate UIs.
            try {
                const PDFFile = await axios.get(`/api/PDFFile/DownloadPDFFile/${pdfType}`, {
                    responseType: 'blob',
                    signal,
                });
                if (!(PDFFile.status === 200 ? PDFFile.data : null)) {
                    return { setPdfUrl };
                }
                const pdfFile = new Blob([PDFFile.data], { type: 'application/pdf' });
                const PDFURL = URL.createObjectURL(pdfFile);
                return { PDFURL, setPdfUrl };
            } catch (error) {
                // Effect cleanup aborts in-flight downloads when Plans unmounts (e.g. app tab change).
                if (
                    axios.isCancel?.(error) ||
                    error?.code === 'ERR_CANCELED' ||
                    error?.name === 'CanceledError' ||
                    error?.name === 'AbortError'
                ) {
                    return { cancelled: true, setPdfUrl };
                }
                throw error;
            }
        },
        onSuccess: (result) => {
            if (!result || result.cancelled || !result.PDFURL) {
                return;
            }
            result.setPdfUrl?.(result.PDFURL);
            dispatch({ type: 'PDF_FILE', payload: result.PDFURL });
        },
        onError: (error) => {
            console.log(error);
        },
    });
}
